import { useSelector } from 'react-redux';
import {
  fetchLinkedMachines,
  selectLinkedMachines,
  setLinkedMachines,
} from '../../../../slices/templates';
import { Button, Empty, Flex, Input, List, message, Spin } from 'antd';
import MachineCheckbox from '../../form/machineCheckbox';
import { Machine } from '../../../../types';
import { ReactNode, useState } from 'react';
import MachineDrawer from '../../form/machineDrawer';
import { selectMachines } from '../../../../slices/machines';
import { linkMachines, unlinkMachine } from '../../../../utils/httpRequest';
import { useAppDispatch } from '../../../../store';
import { Container } from './style';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export interface LinkedMachineProps {
  templateID: string;
  validateLinkMachine?: () => Promise<boolean>;
  header?: ReactNode;
  showSearch?: boolean;
}

const LinkedMachine = ({
  templateID,
  validateLinkMachine,
  header,
  showSearch,
}: LinkedMachineProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [showMachineDrawer, setShowMachineDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const linkedMachines = useSelector(selectLinkedMachines);

  const machines = useSelector(selectMachines);
  const [messageApi, contextHolder] = message.useMessage();

  const handleLinkMachines = async () => {
    try {
      let validated = true;
      if (validateLinkMachine) {
        validated = await validateLinkMachine();
      }
      if (validated) {
        setShowMachineDrawer(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleMachinesSelected = async (machineIds: string[]) => {
    try {
      setShowMachineDrawer(false);
      setLoading(true);
      const selectedMachines = machines.filter((m) =>
        machineIds.includes(m.machineID)
      );
      const { data } = await linkMachines({
        machines: selectedMachines || [],
        templateID,
      });

      dispatch(setLinkedMachines(data));
    } catch (err: any) {
      messageApi.error(err?.message || 'Link Machine failed!');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMachine = async (machine: Machine) => {
    try {
      setLoading(true);
      await unlinkMachine(machine.machineID);
      dispatch(fetchLinkedMachines(templateID));
    } catch (err: any) {
      messageApi.error(err?.message || 'Unlink Machine failed!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Container>
        {contextHolder}
        {header}
        <Flex>
          {showSearch && (
            <Flex flex={1}>
              <Input.Search
                placeholder="Search for Machine"
                allowClear
                style={{ width: 400 }}
                enterButton={<SearchOutlined />}
              />
            </Flex>
          )}
          <Button type="primary" ghost onClick={handleLinkMachines}>
            {t('link_machine')}
          </Button>
        </Flex>
        <List>
          {linkedMachines?.length ? (
            linkedMachines?.map((m) => (
              <List.Item key={m?.machineID}>
                <MachineCheckbox
                  machine={m as Machine}
                  deletable
                  onDelete={handleDeleteMachine}
                />
              </List.Item>
            ))
          ) : (
            <Empty style={{ marginTop: 80 }} description="No Machines" />
          )}
        </List>
        <MachineDrawer
          open={showMachineDrawer}
          onClose={() => setShowMachineDrawer(false)}
          onConfirm={handleMachinesSelected}
        />
      </Container>
    </Spin>
  );
};

export default LinkedMachine;
